import { gql } from '@apollo/client';

/**
 * ТЗ 0088 — фрагменты раздела «Маркетинг».
 *
 * Показатели выписаны один раз: «Пульс», таблица разреза, ряды и итог обязаны
 * получать один и тот же набор полей — иначе прочерк в одной плитке и цифра в
 * соседней означали бы разные вещи. Фрагменты НЕ вложены друг в друга: запрос
 * подключает каждый сам (вложенная интерполяция дублировала бы определения в
 * документе).
 *
 * `spendCoveredPln` / `spendCoveredThrough` — расход только за собранные дни
 * и последний такой день: окно, кончающееся сегодня, почти всегда без двух
 * последних дней, и `spendPln` у него null. Экран показывает частичный
 * расход приглушённо с подписью; CPL, ROAS и стоимость продажи из него не
 * считаются никогда.
 *
 * `*Covered` + `ratiosThrough` (проход математики 29.09) — CPL, стоимость
 * продажи и ROAS за собранные дни: числитель и знаменатель — за одни и те же
 * дни, заполнены ТОЛЬКО когда основное отношение null (расход неполный).
 * `crmLeadsRepeat` — повторные заявки того же человека на тот же курс
 * (≤ 30 дней): не CRM-лиды, показываются рядом.
 */
export const MARKETING_METRICS_FIELDS = gql`
  fragment MarketingMetricsFields on MarketingMetrics {
    spendPln
    spendCoveredPln
    spendCoveredThrough
    impressions
    clicks
    ctr
    cpc
    cpm
    leadsPlatform
    crmLeads
    crmLeadsRepeat
    touchesOther
    sales
    revenuePln
    cashPln
    cplPln
    costPerSalePln
    roas
    roasCash
    ratiosThrough
    cplCoveredPln
    costPerSaleCoveredPln
    roasCovered
    roasCashCovered
  }
`;

/** Свежесть источника: чипы «Пульса», «Качества» и таблица «Настроек». */
export const MARKETING_SOURCE_STATUS_FIELDS = gql`
  fragment MarketingSourceStatusFields on MarketingSourceStatus {
    source
    mode
    coveredFrom
    coveredTo
    completeThrough
    lastSuccessAt
    lastError
    running
  }
`;

/*
 * Справочники. Каждый — со своим `id`: ответ мутации «сохранить правило»
 * обновляет ту же запись кэша, из которой читает таблица «Настроек».
 */

export const MARKETING_CHANNEL_RULE_FIELDS = gql`
  fragment MarketingChannelRuleFields on MarketingChannelRuleType {
    id
    priority
    field
    match
    pattern
    channel
    subChannel
    isPaid
    active
    note
  }
`;

export const MARKETING_COURSE_ALIAS_FIELDS = gql`
  fragment MarketingCourseAliasFields on MarketingCourseAliasType {
    id
    kind
    value
    courseId
    priority
  }
`;

export const MARKETING_OFFER_RULE_FIELDS = gql`
  fragment MarketingOfferRuleFields on MarketingOfferRuleType {
    id
    priority
    field
    regex
    offer
  }
`;

export const MARKETING_PIPELINE_FIELDS = gql`
  fragment MarketingPipelineFields on MarketingPipelineType {
    id
    kommoId
    name
    funnelKind
    funnelKindLocked
  }
`;

/*
 * Этап 2. Строки плана, сигналы и сопоставления кампаний — настоящие
 * записи со своим `id`: ответ мутации и перечитанный список сходятся в одну
 * запись кэша.
 */

export const MARKETING_TARGET_FIELDS = gql`
  fragment MarketingTargetFields on MarketingTargetType {
    id
    month
    channel
    familySlug
    metric
    value
    note
    updatedAt
  }
`;

/**
 * `metrics` и `params` — JSONString: сервер отдаёт их СТРОКОЙ JSON, разбор —
 * в доменном хуке (`marketingParseJson`).
 */
export const MARKETING_SIGNAL_FIELDS = gql`
  fragment MarketingSignalFields on MarketingSignalType {
    id
    kind
    severity
    status
    entityId
    entityName
    familySlug
    channel
    windowFrom
    windowTo
    metrics
    params
    firstSeen
    lastSeen
    ackBy
  }
`;

export const MARKETING_CAMPAIGN_ALIAS_FIELDS = gql`
  fragment MarketingCampaignAliasFields on MarketingCampaignAliasType {
    id
    platform
    field
    rawValue
    entityId
    entityName
    createdBy
    createdAt
  }
`;

/**
 * Этап 3 — строка поиска (`MarketingSearchRow`): запрос / страница / пара /
 * страна / устройство с прошлым окном. `host` — хост страницы (у пары —
 * страницы `key2`); у запросов, стран и устройств — NULL. `prev*` NULL — прошлое окно собрано
 * не целиком (пропуск ≠ 0); 0 — собрано, а ключа в нём не было. Нужна и
 * отчёту поисковика, и топ-запросам спроса.
 */
export const MARKETING_SEARCH_ROW_FIELDS = gql`
  fragment MarketingSearchRowFields on MarketingSearchRow {
    key
    key2
    host
    familySlug
    brand
    clicks
    impressions
    ctr
    position
    prevClicks
    prevImpressions
    prevPosition
  }
`;
